import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import { RootState } from '../index'

export interface FlowMetricCounts {
  unique: number
  total: number
}

export interface OnboardingStatsByFlow {
  startup: {
    rfpSubmitted: FlowMetricCounts
    teamSelectionSubmitted: FlowMetricCounts
    v0CodeCreated: number
    emailRegistrations: FlowMetricCounts
    orgProjectNamed: FlowMetricCounts
  }
  scope: {
    rfpSubmitted: FlowMetricCounts
    teamSelectionSubmitted: FlowMetricCounts
    additionalScopeSubmitted: FlowMetricCounts
    emailRegistrations: FlowMetricCounts
    orgProjectNamed: FlowMetricCounts
  }
  reshuffle: {
    rfpSubmitted: FlowMetricCounts
    teamSelectionSubmitted: FlowMetricCounts
    additionalScopeSubmitted: FlowMetricCounts
    emailRegistrations: FlowMetricCounts
    orgProjectNamed: FlowMetricCounts
  }
  riskintelligence?: {
    rfpSubmitted: FlowMetricCounts
    teamSelectionSubmitted: FlowMetricCounts
    emailRegistrations: FlowMetricCounts
    orgProjectNamed: FlowMetricCounts
  }
  plan?: {
    rfpSubmitted: FlowMetricCounts
    teamSelectionSubmitted: FlowMetricCounts
    additionalScopeSubmitted: FlowMetricCounts
    emailRegistrations: FlowMetricCounts
    orgProjectNamed: FlowMetricCounts
  }
  trial?: {
    rfpSubmitted: FlowMetricCounts
    emailRegistrations: FlowMetricCounts
    orgProjectNamed: FlowMetricCounts
    projectsCreated: FlowMetricCounts
    creditsPurchased: FlowMetricCounts
    collaboratorSeatsPurchased: FlowMetricCounts
  }
  check?: {
    checkLandingPageView: FlowMetricCounts
    checkSessionStarted: FlowMetricCounts
    moAccountRegistered: FlowMetricCounts
    checkSlackConnected: FlowMetricCounts
    checkGithubConnected: FlowMetricCounts
    checkCreditPurchased: FlowMetricCounts
  }
  motiocomp?: {
    motiocompLandingPageView: FlowMetricCounts
    motiocompSessionStarted: FlowMetricCounts
    motiocompAccountRegistered: FlowMetricCounts
    motiocompTicketCompiled: FlowMetricCounts
    motiocompOutputCheckRun: FlowMetricCounts
  }
  /** /pm "execution" flow (regular Motionode signup, dedicated landing) */
  pm?: {
    pmLandingPageView: FlowMetricCounts
    pmAccountRegistered: FlowMetricCounts
    pmOrgProjectNamed: FlowMetricCounts
    pmDeliverablesReached: FlowMetricCounts
  }
}

export interface OnboardingTableRow {
  id: number
  flow: string
  user_id: number
  email: string | null
  resources_email: string | null
  organization_name: string | null
  project_name: string | null
  /** Display name from sys_users */
  user_name: string | null
  rating: string | null
  comments: string | null
  origin: string | null
  source: string | null
  ip_address: string | null
  ip_city: string | null
  ip_region: string | null
  ip_country: string | null
  /** desktop_windows | desktop_mac | ios | android */
  device: string | null
  /** 1 = loaded demo, 0 or null = not demo */
  loaded_demo: number | null
  /** Latest event_type for this user+flow */
  last_event_type: string | null
  /** Duration in seconds between first and last trial_activity_logs entries for this user (trial flow only) */
  trial_duration_seconds: number | null
  /** Whether user agreed to ToS and Privacy Policy at registration (Mo /mo signup) */
  agreed_to_terms: number | null
  /** Whether user opted in to email updates at registration (Mo /mo signup) */
  accept_email_updates: number | null
  created_at: string
}

export interface TrialActivityEntry {
  id: number
  user_id: number
  action: string
  metadata: Record<string, unknown> | null
  created_at: string
}

export const onboardingApi = createApi({
  reducerPath: 'onboardingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/admin',
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token
      if (token) {
        headers.set('authorization', `Bearer ${token}`)
      }
      return headers
    },
  }),
  tagTypes: ['Onboarding'],
  endpoints: (builder) => ({
    getOnboardingStats: builder.query<{ data: OnboardingStatsByFlow }, void>({
      query: () => '/onboarding/stats',
      providesTags: ['Onboarding'],
    }),
    getOnboardingTable: builder.query<
      { data: OnboardingTableRow[]; meta: { total: number; page: number; limit: number } },
      { page?: number; limit?: number; flow?: string; sortBy?: string; sortOrder?: 'ASC' | 'DESC' }
    >({
      query: (params) => ({
        url: '/onboarding/table',
        params,
      }),
      providesTags: ['Onboarding'],
    }),
    getTrialActivity: builder.query<{ data: TrialActivityEntry[] }, number>({
      query: (userId) => ({
        url: '/onboarding/trial-activity',
        params: { userId },
      }),
      providesTags: ['Onboarding'],
    }),
    deleteTrialActivityEntry: builder.mutation<{ success: boolean }, number>({
      query: (id) => ({
        url: `/onboarding/trial-activity/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Onboarding'],
    }),
    deleteTrialActivityForUser: builder.mutation<{ success: boolean; deleted: number }, number>({
      query: (userId) => ({
        url: `/onboarding/trial-activity/user/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Onboarding'],
    }),
  }),
})

export const {
  useGetOnboardingStatsQuery,
  useGetOnboardingTableQuery,
  useGetTrialActivityQuery,
  useDeleteTrialActivityEntryMutation,
  useDeleteTrialActivityForUserMutation,
} = onboardingApi
